import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubmit } from 'react-router';

import { Assets } from '~/assets.g/assets';
import { CustomButton } from '~/shared/components/custom_button';

interface AuthLayoutProps {
  children: ReactNode;
  error?: string;
  version?: string;
}

interface RegionLanguages {
  [key: string]: {
    name: string;
    languages: Array<{
      code: string;
      name: string;
    }>;
  };
}

const REGIONS: RegionLanguages = {
  'Asia Pacific': {
    name: 'Asia Pacific',
    languages: [
      { code: 'en', name: 'India (English)' },
      { code: 'hi', name: 'India (Hindi)' },
    ],
  },
};

interface LanguageSelectorProps {
  locale: string;
  onLanguageChange: (newLocale: string) => void;
}

function LanguageSelector({ locale, onLanguageChange }: LanguageSelectorProps) {
  const { t } = useTranslation(['auth', 'common']);

  return (
    <div className="w-full">
      <h2 className="h4 text-neutralUltraDark">{t('changeLanguage')}</h2>
      <p className="bodySm mt-1 text-neutralSemiDark">{t('changeLanguageTitle')}</p>
      <div className="h-6" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
        {Object.entries(REGIONS).map(([regionKey, region]) => (
          <div key={regionKey}>
            <h3 className="bodyMdSemiBold mb-2 text-neutralDark">{region.name}</h3>
            <div className="flex flex-col items-start gap-1">
              {region.languages.map((lang) => (
                <CustomButton
                  key={lang.code}
                  buttonType="text"
                  variant={lang.code === locale ? 'primary' : 'surface'}
                  className="bodySm"
                  onClick={() => onLanguageChange(lang.code)}
                >
                  {lang.name}
                </CustomButton>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

interface AuthContentPanelProps {
  children: ReactNode;
  error?: string;
}

function AuthContentPanel({ children, error }: AuthContentPanelProps) {
  const { t } = useTranslation(['auth', 'common']);

  return (
    <div
      className="relative z-20 flex w-full max-w-[480px] flex-col items-center overflow-y-auto rounded-xl bg-white px-4 py-6 shadow-lg transition-all duration-300 ease-in-out md:rounded-2xl md:px-8 md:py-8"
      style={{ maxHeight: 'calc(100vh - 80px)' }}
    >
      {error && (
        <div className="bodyMd mb-6 w-full rounded-xl border border-l-4 border-btnColor border-l-btnColor bg-[#C6343414] px-4 py-2.5 text-fontColor">
          <div className="flex items-center justify-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-btnColor" />
            <p className="bodySm">{error}</p>
          </div>
        </div>
      )}

      <div className="flex w-full flex-col items-center">
        <div className="w-full">{children}</div>
      </div>

      {/* <div className="flex w-full max-w-[520px] justify-between px-4 pb-6">
        <CustomButton
          buttonType="text"
          variant="surface"
          className="bodySm"
        >
          {t('contactUs')}
        </CustomButton>
         <CustomButton
          buttonType="text"
          variant="surface"
          className="bodySm flex items-center gap-2"
          onClick={handleLanguageClick}
        >
          <GlobeAltIcon className="h-4 w-4" />
          {t('changeLanguage')}
        </CustomButton> 
      </div> */}

      {/* {openModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-6">
          <div className="relative flex h-[335px] w-full overflow-auto rounded-md bg-neutralWhite shadow-lg md:w-[683px]">
            <div className="flex-grow px-8 py-6">
              <LanguageSelector />
            </div>
            <button
              className="absolute right-2 top-2 p-2"
              onClick={handleLanguageClose}
            >
              <XMarkIcon className="h-6 w-6 text-neutralDark" />
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
}

export function AuthLayout({ children, error, version }: AuthLayoutProps) {
  const [openModal, setOpenModal] = useState(false);
  const { t, i18n } = useTranslation(['auth', 'common']);
  const submit = useSubmit();
  const locale = i18n.language;

  const changeLocale = (value: string) => {
    i18n.changeLanguage(value).then(() => {
      const formData = new FormData();
      formData.set('lng', value);
      submit(formData);
    });
  };

  const handleLanguageClick = () => setOpenModal(true);
  const handleLanguageClose = () => setOpenModal(false);
  const handleLanguageChange = (newLocale: string) => {
    changeLocale(newLocale);
    handleLanguageClose();
  };

  return (
    <div className="signup-bg flex h-dvh w-full items-center justify-center overflow-x-hidden bg-primarySemiDark bg-cover bg-center bg-no-repeat px-4">
      <div className="w-full max-w-[480px]">
        {/* <img
          src={Assets.images.logo}
          alt="Raithu logo"
          className="mx-auto my-5"
        /> */}
        <AuthContentPanel error={error}>{children}</AuthContentPanel>
      </div>
      <footer className="fixed bottom-5 left-0 z-10 block w-full py-1 text-center text-neutralDefault">
        ©{new Date().getFullYear()}
        {''}
        {t('rightsReserved')} {'v'}
        {version}
      </footer>
    </div>
  );
}
